import type { CombatantId } from "./combatant";
import type { RoomRect } from "./rooms";
import type { Side } from "./side";

/**
 * The encounter's scene-wide setup: its environment tag and its sitrep (the printed
 * victory condition plus the map zones that condition reads).
 */

/**
 * The encounter's **environment/hazard tag** — a closed, GM-set enum naming a scene-wide
 * condition the effects DSL's `SceneEnvironmentPredicate` reads. Distinct from the
 * per-cell "dangerous terrain" tile brush (an unrelated per-cell hazard) and from the
 * "scene" *cadence* grain on a frozen affordance's `cap`/`current.affordanceState` — every other "scene"
 * reference in this model names that per-cadence substrate, not an environment fact.
 * Closed rather than a free-text tag because the DSL predicate that reads it must
 * pattern-match a known value, the same posture {@link "@lancer-vtt/shared".GridMode}
 * takes. Extend the union and {@link ENCOUNTER_ENVIRONMENTS} together.
 *
 * **`"zero_g"` covers RAW's "low OR zero gravity" category, not zero-gravity alone** —
 * RAW never distinguishes the two ("low or zero gravity and underwater environments",
 * "low or zero gravity or space"), so this value is the one closed variant for that whole
 * RAW category; there is no separate `"low_g"` to confuse it with.
 */
export type EncounterEnvironment = "zero_g" | "underwater";

/** Every {@link EncounterEnvironment}, with a display label for the GM-facing set-environment control. */
export const ENCOUNTER_ENVIRONMENTS: readonly { value: EncounterEnvironment; label: string }[] = [
  { value: "zero_g", label: "Zero-G" },
  { value: "underwater", label: "Underwater" },
];

/** Whether a string is a known {@link EncounterEnvironment}. */
export function isEncounterEnvironment(v: string): v is EncounterEnvironment {
  return ENCOUNTER_ENVIRONMENTS.some((e) => e.value === v);
}

/**
 * A **sitrep zone kind** (Core pp. 267–273) — what an authored {@link SitrepZone} means
 * to the victory condition it belongs to: `"control"` (a Control Zone — Control/Recon's
 * contested CZs, Holdout's single CZ, Gauntlet's EDZ-fortified CZ),
 * `"deploy_pc"`/`"deploy_enemy"` (starting deployment areas), `"extraction"`
 * (Escort/Extraction's EZ), `"ingress"` (Escort/Extraction's objective entry point),
 * `"objective"` (where an Escort/Extraction objective spawns), `"clear"` (the `clear`
 * sitrep's target area — the ground that must be flattened).
 *
 * **`"clear"` is deliberately its own kind rather than a reuse of `"objective"`** —
 * `"objective"` means "where an Escort/Extraction objective *spawns*", a spawn-point
 * marker with no state read out of it after setup, whereas a `"clear"` zone is
 * continuously scored terrain. Overloading one kind with both readings would make every
 * "is this zone an objective zone" test ambiguous about which of the two it meant.
 */
export const SITREP_ZONE_KINDS = [
  "clear",
  "control",
  "deploy_pc",
  "deploy_enemy",
  "extraction",
  "ingress",
  "objective",
] as const;

/**
 * The kind union, **derived from {@link SITREP_ZONE_KINDS}** rather than written out
 * beside it. A CLI that validates an authored `--zone ...:<kind>` needs the values at
 * runtime, and a hand-maintained second list is exactly the pair that agrees until
 * someone adds a kind to one of them.
 */
export type SitrepZoneKind = (typeof SITREP_ZONE_KINDS)[number];

/**
 * One authored **zone** of a {@link SitrepConfig} — a named region of the map the victory
 * predicate reads. **Reuses the {@link RoomRect} geometry vocabulary only** — a union of
 * axis-aligned rects in cell coordinates — never {@link EncounterState.rooms} itself,
 * which is load-bearing for the "rooms carry no mechanical meaning" invariant. A sitrep
 * zone is the mechanical mirror rooms deliberately are not: `@lancer-vtt/rules`'
 * zone-geometry primitives (`unitsInZone`/`controlOf`) read it to compute
 * occupancy/control.
 *
 * `side` marks a deployment zone's owning side (`deploy_pc`/`deploy_enemy`); unused by
 * the other kinds. `hidden` marks Recon's True CZ — a GM-only concealed designation that
 * must stay off the player-visible projection.
 */
export type SitrepZone = {
  id: string;
  kind: SitrepZoneKind;
  rects: RoomRect[];
  side?: Side;
  hidden?: boolean;
};

/**
 * A **sitrep type** — one of Lancer's six printed victory conditions (Core pp. 267–273),
 * each a distinct predicate over zones/rounds/scores, **plus one homebrew seventh,
 * `"clear"`**: flatten the landing zone. `"clear"` is not RAW — it is this campaign's
 * bespoke victory condition, and the only one whose predicate reads *terrain* rather than
 * occupancy or an objective combatant. See {@link SITREP_TYPES} for display labels and
 * {@link isSitrepType} for the runtime guard.
 */
export type SitrepConfig = {
  type: "clear" | "control" | "escort" | "extraction" | "gauntlet" | "holdout" | "recon";
  zones: SitrepZone[];
  roundLimit: number;
  /**
   * The **tracked combatant** this sitrep's predicate reads, if any.
   *
   * - Escort/Extraction: the Objective, spawned via `spawnObject` — it must reach the
   *   extraction zone to win, and its destruction is a draw.
   * - `clear`: the **terraforming rig**. It is an already-spawned NPC, so it is *linked*
   *   to an existing combatant rather than spawned into being (`lvtt encounter sitrep
   *   link-objective <combatantId>`, not `spawn-objective`). Its destruction is an
   *   outright **loss**, not a draw: without the rig nothing can flatten the ground.
   */
  objectiveId?: CombatantId;
};

/** Every {@link SitrepConfig}'s `type`, with a display label for the GM-facing sitrep picker. */
export const SITREP_TYPES: readonly { value: SitrepConfig["type"]; label: string }[] = [
  { value: "clear", label: "Clear" },
  { value: "control", label: "Control" },
  { value: "escort", label: "Escort" },
  { value: "extraction", label: "Extraction" },
  { value: "gauntlet", label: "Gauntlet" },
  { value: "holdout", label: "Holdout" },
  { value: "recon", label: "Recon" },
];

/** Whether a string is a known {@link SitrepConfig} `type`. */
export function isSitrepType(v: string): v is SitrepConfig["type"] {
  return SITREP_TYPES.some((t) => t.value === v);
}

/**
 * **The only path-dependent sitrep state** — Control's per-round accumulated score, one
 * entry per {@link Side}. Every other sitrep type is a pure end-of-round-N (or
 * continuous-check) snapshot over `combatants`/`activation.round`/`sitrep` and needs no
 * stored progress. Seeded `{ pc: 0, npc: 0 }` by `set_sitrep` when
 * `sitrep.type === "control"`; absent for every other type.
 */
export type SitrepProgress = { scores: Record<Side, number> };
